import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { forkJoin } from 'rxjs';
import { take } from 'rxjs/operators';
import { DeliveryNoteService } from '../../services/delivery-note.service';
import { DeliveryNote, LineSuggestions, STATUS_BADGES, STATUS_LABELS, newClientRef, ukDate } from '../../models/delivery-note.models';

interface PickRow {
  warehouseLocationId: number | null;
  batchNumber: string;
  qty: number;
}

interface PickLine extends LineSuggestions {
  rows: PickRow[];
}

/**
 * Pick a delivery note: each outstanding line starts with the suggested locations (oldest stock first) as
 * pick rows, which the picker changes to what they actually picked - another location, a batch, a split, or
 * less (short). Confirm books it all out as Goods Out (PICK) in one go.
 */
@Component({
    selector: 'app-picking-slip',
    templateUrl: './picking-slip.component.html',
    standalone: false
})
export class PickingSlipComponent implements OnInit {
  readonly statusLabels = STATUS_LABELS;
  readonly statusBadges = STATUS_BADGES;
  readonly ukDate = ukDate;

  note: DeliveryNote | null = null;
  lines: PickLine[] = [];
  locations: { id: number; label: string }[] = [];
  loading: boolean = true;
  busy: boolean = false;
  error: string = '';
  success: string = '';
  /** Kept until a confirm succeeds, so pressing Confirm again after a dropped reply cannot book twice. */
  private clientRef = newClientRef();

  constructor(private service: DeliveryNoteService, private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.service.locations().pipe(take(1)).subscribe({ next: (l) => this.locations = l, error: () => this.locations = [] });
    this.load();
  }

  get id(): number {
    return Number(this.route.snapshot.paramMap.get('id'));
  }

  get open(): boolean {
    return !!this.note && (this.note.status === 'ready' || this.note.status === 'part_picked');
  }

  load(): void {
    forkJoin([this.service.get(this.id), this.service.suggestions(this.id)]).pipe(take(1)).subscribe({
      next: ([note, suggestions]) => {
        this.note = note;
        this.lines = suggestions.map((s) => ({
          ...s,
          rows: s.suggestions.length
            ? s.suggestions.map((g) => ({ warehouseLocationId: g.warehouseLocationId, batchNumber: g.batch || '', qty: g.qty }))
            : [{ warehouseLocationId: null, batchNumber: '', qty: 0 }],
        }));
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.error = 'The picking slip could not be loaded.';
      },
    });
  }

  lineName(lineId: number): string {
    const l = this.note.lines.find((x) => x.id === lineId);
    return l ? (l.description || l.productName) : '';
  }

  addRow(line: PickLine): void {
    line.rows = [...line.rows, { warehouseLocationId: null, batchNumber: '', qty: 0 }];
  }

  removeRow(line: PickLine, i: number): void {
    line.rows = line.rows.filter((_, x) => x !== i);
  }

  picked(line: PickLine): number {
    return Math.round(line.rows.reduce((s, r) => s + (Number(r.qty) || 0), 0) * 1000) / 1000;
  }

  /** Nothing picked on every line: the confirm button stays off. */
  get anything(): boolean {
    return this.lines.some((l) => l.rows.some((r) => Number(r.qty) > 0));
  }

  confirm(): void {
    this.error = '';
    this.success = '';
    for (const line of this.lines) {
      if (this.picked(line) > line.outstanding) {
        this.error = `${line.productName}: picking ${this.picked(line)} but only ${line.outstanding} is outstanding.`;
        return;
      }
      if (line.rows.some((r) => Number(r.qty) > 0 && !r.warehouseLocationId)) {
        this.error = `${line.productName}: choose the location each quantity was picked from.`;
        return;
      }
    }
    const picks = this.lines.flatMap((l) => l.rows.filter((r) => Number(r.qty) > 0).map((r) => ({
      lineId: l.lineId, warehouseLocationId: r.warehouseLocationId, batchNumber: r.batchNumber || undefined, qty: Number(r.qty),
    })));
    this.busy = true;
    this.service.pick(this.id, this.clientRef, picks).pipe(take(1)).subscribe({
      next: (note) => {
        this.busy = false;
        this.clientRef = newClientRef();
        this.success = note.status === 'picked' ? 'Everything is picked – it can be despatched.' : 'Pick booked. Lines left short stay on the slip.';
        this.load();
      },
      error: (e) => {
        this.busy = false;
        this.error = (e && e.error && e.error.message) || 'The pick was not booked. Please try again.';
        if (e && e.status === 409) {
          // the reference was used for a different pick: start afresh from the current slip
          this.clientRef = newClientRef();
          this.load();
        }
      },
    });
  }

  despatch(): void {
    const short = this.note.lines.some((l) => (l.outstanding || 0) > 0);
    if (!confirm(short ? 'Some lines are short. Despatch what has been picked and close the rest short?' : 'Mark this delivery note as despatched?')) {
      return;
    }
    this.busy = true;
    this.service.action(this.id, 'despatch').pipe(take(1)).subscribe({
      next: () => {
        this.busy = false;
        this.success = 'Despatched.';
        this.load();
      },
      error: (e) => {
        this.busy = false;
        this.error = (e && e.error && e.error.message) || 'Could not despatch.';
      },
    });
  }

  print(): void {
    this.service.pdf(this.id, 'picking-slip').pipe(take(1)).subscribe({
      next: (url) => window.open(url, '_blank'),
      error: () => this.error = 'The picking slip could not be printed.',
    });
  }
}
